/**
 * Relay load-type option filtering for the Panel Type dropdown.
 *
 * Determines which RelayLoadType options are valid for the in-scope
 * customer type and relay position. Separated from PanelTypeField so the
 * predicate logic can be unit-tested without a React/Redux context.
 *
 * Contains:
 * - filterRelayLoadTypeOptions — the pure filter predicate used by PanelTypeField
 */

/**
 * Load-type ids (RelayLoadType.id) permitted on commercial relay positions 2 and 4.
 * This is a hardware relay-capability constraint, independent of customer-type
 * availability.
 */
const RELAY_2_4_COMMERCIAL_LOAD_TYPE_IDS = [2, 6];

/**
 * Returns true when a RelayLoadType option should appear in the dropdown for
 * the in-scope customer type and relay position. An option is offered when:
 *   1. opt.applies_to_loads is true (or undefined — fail-open for old records)
 *   2. availableLoadTypeIds is empty (the customer type has no configured set
 *      yet — staff curation incomplete) OR the option's id is a member of
 *      availableLoadTypeIds. An empty set falls open to the full catalog so
 *      the dropdown never goes empty.
 *   3. Commercial sites on relay positions 2 or 4 are further restricted to
 *      RELAY_2_4_COMMERCIAL_LOAD_TYPE_IDS, a hardware relay-position
 *      constraint independent of customer-type availability.
 *
 * @param {{ id: number, applies_to_loads?: boolean }} opt
 * @param {{ availableLoadTypeIds: number[], relayNumber?: number|string, isBillboard: boolean }} context
 * @returns {boolean}
 */
export function filterRelayLoadTypeOptions(opt, { availableLoadTypeIds, relayNumber, isBillboard }) {
  // applies_to_loads: treat undefined as true (fail-open)
  if (opt.applies_to_loads === false) {
    return false;
  }

  if (availableLoadTypeIds.length > 0 && !availableLoadTypeIds.includes(opt.id)) {
    return false;
  }

  // Commercial relay-position narrowing (hardware relay-capability constraint)
  if (!isBillboard && (relayNumber == 2 || relayNumber == 4)) {
    return RELAY_2_4_COMMERCIAL_LOAD_TYPE_IDS.includes(opt.id);
  }

  return true;
}
